import { VALIDATION_MESSAGES, optionalNumber } from '@zorro/common-utils';
import * as yup from 'yup';

export const ENROLLMENT_TEAM_RESPONSIBILITIES_MAX_LENGTH = 500;

export const getMonthlySEPDeadlineOptions = () => {
  const options = Array.from({ length: 28 }, (_, index) => ({
    label: (index + 1).toString(),
    value: (index + 1).toString(),
  }));
  options.push({
    label: 'Last day of the month',
    value: '0',
  });
  return options;
};

export const parseMonthlySEPDeadlineValuesFromOptions = (
  optionValue: string
) => {
  if (optionValue === '0') {
    return {
      isMonthlySEPDeadlineEndOfMonth: true,
      monthlySEPDeadline: null,
    };
  }
  return {
    isMonthlySEPDeadlineEndOfMonth: false,
    monthlySEPDeadline: Number.parseInt(optionValue),
  };
};

const baseZorroSetupSchema = {
  producerId: yup.string().nullable(),
  enrollmentTeamIds: yup
    .array()
    .of(yup.string().required())
    .required(VALIDATION_MESSAGES.enrollmentTeamRequired),
  estimatedEmployees: yup
    .number()
    .nullable()
    .transform(optionalNumber)
    .min(1, VALIDATION_MESSAGES.estimatedEmployeesMin),
  estimatedParticipationRate: yup
    .number()
    .nullable()
    .transform(optionalNumber)
    .min(1, VALIDATION_MESSAGES.estimatedParticipationRateMin)
    .max(100, VALIDATION_MESSAGES.estimatedParticipationRateMax),
};

const finalizationModeZorroSetupSchema = {
  producerId: yup.string().required(VALIDATION_MESSAGES.producerRequired),
  enrollmentTeamIds: yup
    .array()
    .of(yup.string().required())
    .min(1, VALIDATION_MESSAGES.enrollmentTeamRequired)
    .required(VALIDATION_MESSAGES.enrollmentTeamRequired),
  estimatedEmployees: yup
    .number()
    .min(1, VALIDATION_MESSAGES.estimatedEmployeesRequired)
    .typeError(VALIDATION_MESSAGES.estimatedEmployeesRequired)
    .positive(VALIDATION_MESSAGES.estimatedEmployeesRequired)
    .required(VALIDATION_MESSAGES.estimatedEmployeesRequired),
  estimatedParticipationRate: yup
    .number()
    .min(1, VALIDATION_MESSAGES.estimatedParticipationRateMin)
    .max(100, VALIDATION_MESSAGES.estimatedParticipationRateMax)
    .typeError(VALIDATION_MESSAGES.estimatedParticipationRateRequired)
    .positive(VALIDATION_MESSAGES.estimatedParticipationRateRequired)
    .required(VALIDATION_MESSAGES.estimatedParticipationRateRequired),
};

export const getZorroSetupSchemaObject = (
  isFinalizationMode?: boolean,
  isProspectMode?: boolean
) =>
  yup.object({
    zorroPartnerId: yup
      .string()
      .required(VALIDATION_MESSAGES.zorroPartnerRequired),
    ccEmailList: yup
      .array()
      .of(yup.string().email(VALIDATION_MESSAGES.emailsValid).required())
      .required(VALIDATION_MESSAGES.emailRequired),
    crmRecordId: yup.number().nullable().transform(optionalNumber),
    monthlySEPDeadline: yup
      .string()
      .required(VALIDATION_MESSAGES.monthlySEPDeadlineRequired),
    consultationLink: yup
      .string()
      .url(VALIDATION_MESSAGES.consultationLinkValid)
      .max(255, VALIDATION_MESSAGES.consultationLinkMax),
    enrollmentTeamResponsibilities: yup
      .string()
      .max(
        ENROLLMENT_TEAM_RESPONSIBILITIES_MAX_LENGTH,
        VALIDATION_MESSAGES.enrollmentTeamResponsibilitiesMax
      ),
    producerAgentId: yup.string().nullable(),
    csmUserId: yup.string().nullable(),
    ...(isFinalizationMode
      ? finalizationModeZorroSetupSchema
      : baseZorroSetupSchema),
    ...(isProspectMode
      ? {
          prospectCoverageStartDate: yup
            .date()
            .required(VALIDATION_MESSAGES.coverageStartDate)
            .typeError(VALIDATION_MESSAGES.coverageStartDate),
        }
      : { prospectCoverageStartDate: yup.date() }),
    enrollmentInstructions: yup.string(),
    isPayrollSyncOn: yup.boolean(),
    isPartnersHubOn: yup.boolean(),
    isEmploymentSyncOn: yup.boolean(),
  });

export type ZorroSetupFormFields = yup.InferType<
  ReturnType<typeof getZorroSetupSchemaObject>
>;
